import styled from 'styled-components';
import { Modal, Button, Table } from 'react-bootstrap';

/**
 * Utility function to determine text contrast color
 */
export function getContrast(color) {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
}

/**
 * STYLED COMPONENTS
 */

export const CropContainer = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: 16px;
    border-radius: 8px;
    overflow: hidden;
    background: white;
`;

export const PreviewContainer = styled.div`
    background: white;
    padding: 12px;
    border-radius: 8px;
    text-align: center;
    margin-top: 12px;

    canvas {
        max-width: 100%;
        height: auto;
        border-radius: 6px;
        border: 1px solid #e0e0e0;
    }
`;

export const StyledButton = styled(Button)`
    border-radius: 6px;
    font-weight: 600;
    padding: 8px 16px;
    transition: all 0.3s ease;
    border: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &.btn-info {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
        }
    }
`;

export const ModernModal = styled(Modal)`
    .modal-dialog {
        max-width: 1200px;
    }

    .modal-content {
        background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        border: none;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        overflow: hidden;
    }

    .modal-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        padding: 16px 20px;

        .modal-title {
            font-weight: 700;
            font-size: 18px;
            color: white;
        }

        .btn-close {
            filter: brightness(0) invert(1);
        }
    }

    .modal-body {
        background: #f8f9fb;
        padding: 20px;
        max-height: 85vh;
        overflow-y: auto;
    }

    .modal-footer {
        background: #ffffff;
        border-top: 1px solid #e0e0e0;
        padding: 16px;
        gap: 12px;
    }
`;

export const GarmentTable = styled(Table)`
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 0;

    thead {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

        th {
            color: white;
            font-weight: 600;
            border: none;
            padding: 8px 6px;
            text-transform: uppercase;
            font-size: 11px;
            letter-spacing: 0.3px;

            &:hover {
                background: rgba(255, 255, 255, 0.1);
            }
        }
    }

    tbody tr {
        transition: all 0.3s ease;

        &:hover {
            background: #f0f2f5;
        }

        td {
            padding: 6px 6px;
            vertical-align: middle;
            font-size: 12px;
        }
    }
`;

export const SortIcon = styled.span`
    font-size: 12px;
    margin-left: 4px;
    opacity: 0.7;
`;

export const ActionButtonGroup = styled.div`
    display: flex;
    gap: 3px;

    button {
        padding: 3px 8px;
        font-size: 10px;
        font-weight: 600;
        border-radius: 4px;
        transition: all 0.2s ease;

        &:hover {
            transform: translateY(-1px);
        }
    }
`;

export const ActorBadge = styled.span`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 12px;
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    color: white;
    font-weight: 600;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

export const ColorBadge = styled.span`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 12px;
    background: ${props => props.color || '#ccc'};
    color: ${props => getContrast(props.color) ? 'white' : 'black'};
    font-weight: 600;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

export const CategoryBadge = styled.span`
    display: inline-block;
    padding: 4px 8px;
    border-radius: 12px;
    background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
    color: white;
    font-weight: 600;
    font-size: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

export const CompactDetails = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    align-items: center;
`;

export const DetailBadge = styled.span`
    display: inline-block;
    padding: 3px 6px;
    border-radius: 8px;
    background: #e3f2fd;
    color: #1976d2;
    font-weight: 500;
    font-size: 9px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const GarmentDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
`;

export const DetailItem = styled.div`
    padding: 4px 0;
    color: #555;
    
    strong {
        color: #333;
        font-weight: 600;
        margin-right: 6px;
    }
`;

export const GarmentImagePreview = styled.img`
    position: fixed;
    display: block;
    z-index: 200;
    left: 40%;
    top: 5%;
    width: 200px;
    height: 200px;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

export const ImagePreviewSmall = styled.img`
    margin-top: 10px;
    width: 100%;
    max-width: 200px;
    height: auto;
    border-radius: 6px;
    object-fit: cover;
`;

export const ImageThumbnail = styled.img`
    width: 40px;
    height: 40px;
    border-radius: 3px;
    object-fit: cover;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid #ddd;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
`;
